import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiActivity, FiDownload, FiBriefcase } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { resumeApi, diagnosisApi } from '../../services/api';

function extractTextFromContent(content) {
  if (!content) return '';
  const parts = [];
  if (content.name) parts.push(`Name: ${content.name}`);
  if (content.summary) parts.push(`Summary: ${content.summary}`);
  if (Array.isArray(content.experience)) {
    parts.push('Experience:');
    content.experience.forEach(exp => {
      if (!exp) return;
      parts.push(`  ${exp.title || ''} at ${exp.company || ''}`);
      const details = exp.details || exp.bullets || [];
      (Array.isArray(details) ? details : []).forEach(d => {
        const text = typeof d === 'string' ? d : (d?.details || '');
        if (text) parts.push(`    - ${text}`);
      });
    });
  }
  if (Array.isArray(content.education)) {
    parts.push('Education:');
    content.education.forEach(edu => {
      if (!edu) return;
      parts.push(`  ${edu.degree || ''} at ${edu.school || edu.institution || ''}`);
    });
  }
  if (content.skills) {
    const skills = Array.isArray(content.skills) ? content.skills : [];
    const names = skills.map(s => (typeof s === 'string' ? s : s?.name || '')).filter(Boolean);
    if (names.length) parts.push(`Skills: ${names.join(', ')}`);
  }
  return parts.join('\n');
}

const ResumeActionTooltip = ({ show: initialShow, onDownload, resumeId }) => {
  const [show, setShow] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (initialShow) {
      const timer = setTimeout(() => setShow(true), 500);
      return () => clearTimeout(timer);
    }
  }, [initialShow]);

  const handleDownloadClick = () => {
    if (onDownload) {
      onDownload();
      setShow(false);
      return;
    }
    setShow(false);
  };

  const handleDiagnosis = async () => {
    if (!resumeId) {
      toast.error('Resume ID not available');
      return;
    }
    setDiagnosing(true);
    let resume_text = '';
    try {
      const versionRes = await resumeApi.getFirstResumeVersion(resumeId);
      resume_text = extractTextFromContent(versionRes?.data?.content);
      if ((resume_text || '').trim().split(/\s+/).filter(Boolean).length < 80) {
        toast.error('Resume is too short for diagnosis (min 80 words)');
        setDiagnosing(false);
        return;
      }
    } catch (err) {
      toast.error('Could not load resume for diagnosis');
      setDiagnosing(false);
      return;
    }
    try {
      const res = await diagnosisApi.runQuickDiagnosis({
        resume_text,
        target_roles: [],
        seniority: 'Not specified',
        location: 'Not specified',
        channel_preference: 'online',
        visa: null,
        resume_id: resumeId,
        confirmRegenerate: true,
      });
      navigate('/resume-diagnosis/report', { state: { diagnosis: res.data, resumeId } });
      setShow(false);
    } catch (err) {
      toast.error('Diagnosis failed. Please try again.');
    } finally {
      setDiagnosing(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-xl shadow-2xl max-w-md z-50">
      <h3 className="text-lg font-semibold mb-4">What would you like to do next?</h3>
      
      <div className="space-y-4">
        <button
          onClick={handleDownloadClick}
          className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 rounded-lg group transition-all"
        >
          <div className="text-left flex items-center gap-2">
            <FiDownload className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <div>
              <div className="font-medium text-blue-700">Download as PDF</div>
              <div className="text-sm text-blue-600">Export your resume in different visual styles</div>
            </div>
          </div>
          <svg className="w-6 h-6 text-blue-500 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <button
          onClick={handleDiagnosis}
          disabled={diagnosing}
          className="w-full flex items-center justify-between p-4 bg-teal-50 hover:bg-teal-100 rounded-lg group transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <div className="text-left flex items-center gap-2">
            <FiActivity className={`h-5 w-5 text-teal-600 ${diagnosing ? 'animate-spin' : ''}`} />
            <div>
              <div className="font-medium text-teal-700">{diagnosing ? 'Analyzing...' : 'Run Diagnosis'}</div>
              <div className="text-sm text-teal-600">Get AI-powered insights and score for your resume</div>
            </div>
          </div>
          <svg className="w-6 h-6 text-teal-500 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <button
          onClick={() => {
            setShow(false);
            navigate('/jobs/saved');
          }}
          className="w-full flex items-center justify-between p-4 bg-primary-50 hover:bg-primary-100 rounded-lg group transition-all"
        >
          <div className="text-left flex items-center gap-2">
            <FiBriefcase className="h-5 w-5 text-primary-600 flex-shrink-0" />
            <div>
              <div className="font-medium text-primary-700">Apply for Jobs</div>
              <div className="text-sm text-primary-600">Browse and apply to job listings with your updated resume</div>
            </div>
          </div>
          <svg className="w-6 h-6 text-primary-500 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

      </div>

      <button
        onClick={() => setShow(false)}
        className="mt-4 w-full text-gray-500 text-sm hover:text-gray-700"
      >
        Dismiss
      </button>
    </div>
  );
};

export default ResumeActionTooltip;
